import { Round as ArrowForward } from '@manomano-internal/tamagoshi-icons/esm/material/ArrowForward';
import classnames from 'classnames';
import {
  SyntheticEvent,
  useEffect,
  useRef,
  type FunctionComponent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useBooleanFlags } from '@/core/flags/flags.hooks';
import { useLazy } from '@/core/page/hooks/useLazy';
import { pageTypeSelector } from '@/core/page/page.selectors';
import { Link } from '@/core/routing/components/Link';
import { SponsoredLabel } from '@/core/tamagoshiTailwind/components/Label/SponsoredLabel';
import { Gtm } from '@/core/tracking/utils/Gtm';
import { AdviceTag } from '@/digitalAdvice/components/AdviceTag/AdviceTag';
import { GuideCardImage } from '@/digitalAdvice/GuideCard/components/GuideCardImage/GuideCardImage';
import { type GuideCard as GuideCardModel } from '@/digitalAdvice/GuideCard/GuideCard.model';
import { Events } from '@/digitalAdvice/GuideCard/tracking';
import { TipsheetCardVariant } from '@/digitalAdvice/TipsheetCard/constants';
import { displayAdvertiserInformation } from '@/productDiscovery/CommercialAnimation/CommercialAnimation.actions';
import { createAdvertiserInformationPopupContent } from '@/productDiscovery/CommercialAnimation/utils/createAdvertiserInformationPopupContent';
import { StringAbTests } from '@/productDiscovery/ProductPage/constants';
import { getSponsoredLabelText } from '@/sellerAdsManagement/sponsoredLabelText/sponsoredLabelText';

import { linkLabel } from './translations';

import styles from './GuideCard.module.scss';

export type GuideCardProps = GuideCardModel;

export const GuideCard: FunctionComponent<GuideCardProps> = ({
  description,
  image,
  link,
  title,
  sponsor,
}) => {
  const [isProductPageRevampEnabled] = useBooleanFlags([
    StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP,
  ]);
  const dispatch = useDispatch();
  const pageType = useSelector(pageTypeSelector);
  const root = useRef(null);
  const { hasBeenDisplayed } = useLazy(root, true, {
    rootMargin: '0px',
    threshold: 0.2,
  });

  const handleClick = (url: string) => () => {
    Gtm.push(Events.clickCard({ pageType, url, isSponsored: !!sponsor }));
  };

  useEffect(() => {
    if (hasBeenDisplayed) {
      Gtm.push(Events.viewCard({ pageType, isSponsored: !!sponsor }));
    }
  }, [hasBeenDisplayed, pageType, sponsor]);

  const onAdvertiserLabelClicked = async (e: SyntheticEvent) => {
    if (sponsor && sponsor.name) {
      e.preventDefault();
      const adInfo = {
        sponsor: sponsor.name,
        advertiser: sponsor.name,
        userSegments: true,
        dsaPopup: true,
        content: createAdvertiserInformationPopupContent(
          sponsor.name,
          sponsor.name,
          true,
        ),
      };
      dispatch(displayAdvertiserInformation(adInfo));
    }
  };

  return (
    <div
      className={classnames(styles.root, {
        [styles.rootPdpRevamp]: isProductPageRevampEnabled,
      })}
      ref={root}
    >
      {sponsor && (
        <div className={styles.sponsoredLabel}>
          <SponsoredLabel
            label={getSponsoredLabelText()}
            onClick={onAdvertiserLabelClicked}
          />
        </div>
      )}
      <div className={styles.body}>
        <AdviceTag
          data-testid="adviceTag"
          sponsor={sponsor}
          variant={TipsheetCardVariant.LIGHT}
        />
        <h2 className={styles.title}>{title}</h2>
        <div
          className={styles.description}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: `${description}` }}
        />
        <div className={styles.footer}>
          <Link
            className={styles.link}
            href={link}
            onClick={handleClick(link)}
            target="_blank"
            title={linkLabel}
          >
            {linkLabel}
            <ArrowForward className={styles.arrow} />
          </Link>
        </div>
      </div>
      <GuideCardImage className={styles.image} image={image} />
    </div>
  );
};
